import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react'
import useSWRImmutable from 'swr/immutable'
import useYodlee from 'useYodlee'

import {defaultHeaders, fetcher, logBankIntegrationEvent} from '../../../api'
import {useRails} from '../../contexts/rails'

const FastLinkWrapper = ({onSuccess, onFailure, connectedEndpoint, my}) => {
  const {organisation, authenticityToken} = useRails()
  const defaultConnectedEndpoint = `/api/suppliers/${organisation.currentSupplier.id}/banking_providers/connected`
  const resolvedConnectedEndpoint =
    connectedEndpoint ?? defaultConnectedEndpoint

  const logEvent = (eventType, eventBody) => {
    logBankIntegrationEvent({
      authenticityToken,
      eventType,
      eventBody,
      supplierId: organisation.currentSupplier.id,
    })
  }

  const {
    data: yodleeConnectionDetails,
    isLoading: loadingBankConnectionDetails,
  } = useSWRImmutable(
    `/api/suppliers/${organisation.currentSupplier.id}/banking_providers/connection_details`,
    fetcher,
  )

  const {yodleeConfigName, yodleeFastLinkUrl, yodleeToken, abn} =
    yodleeConnectionDetails || {}
  const Yodlee = useYodlee({
    containerId: 'container-fastlink',
    fastLinkOptions: {
      fastLinkURL: yodleeFastLinkUrl,
      abn,
      UserExperienceFlowType: 'Aggregation',
      configName: yodleeConfigName,
      token: {
        tokenType: 'AccessToken',
        tokenValue: yodleeToken,
      },
    },
    onError: (data) => {
      logEvent('onError', data)
    },
    onEvent: (data) => {
      logEvent('onEvent', data)
    },
    onSuccess: (data) => {
      logEvent('onSuccess', data)
    },
    onClose: (data) => {
      logEvent('onClose', data)
    },
    onExit: (data) => {
      logEvent('onExit', data)

      if (data?.sites?.filter((site) => site.status === 'SUCCESS').length > 0) {
        // TODO: Handle 422 responses from connectedEndpoint — this would only occur if saving
        // to the database fails, and is unrelated to Yodlee. In that case we should call
        // onFailure rather than onSuccess.
        fetcher(resolvedConnectedEndpoint, {
          method: 'POST',
          headers: defaultHeaders({authenticityToken}),
        })
        onSuccess(data)
      } else {
        fetcher(
          `/api/suppliers/${organisation.currentSupplier.id}/banking_providers/not_connected`,
          {
            method: 'POST',
            headers: defaultHeaders({authenticityToken}),
            body: JSON.stringify({
              sites_count: data?.sites?.length,
            }),
          },
        )
        onFailure(data)
      }
    },
  })

  if (!loadingBankConnectionDetails && Yodlee.ready && !Yodlee.active) {
    Yodlee.init()
  }

  return (
    <Box
      width={{base: '498px', '2xl': '676px'}}
      my={my}
      display={Yodlee.active ? 'block' : 'none'}
      className="fs-exclude"
    >
      <Box id="container-fastlink" width="100%" />
    </Box>
  )
}

FastLinkWrapper.defaultProps = {
  // null here because the default endpoint requires the supplier ID from useRails,
  // which is only available inside the component. The fallback is handled via ?? inside the component.
  connectedEndpoint: null,
  my: {base: 'sm', lg: 'lg'},
}

FastLinkWrapper.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  connectedEndpoint: PropTypes.string,
  // Chakra vertical-margin token(s); overridable so a host screen can control
  // the gap above/below the Yodlee frame.
  my: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
}

export default FastLinkWrapper
